import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import debounce from "lodash.debounce";
import "./ReArtworkSection.scss";
import Scrollbars from "react-custom-scrollbars-2";
import { resolveIPFS, SUBGRAPH_URL } from "../../Features/Market/Market";
import { useNavigate } from "react-router-dom";

const COLLECTION_NAME_TO_ADDRESS = {
  egypt: "0x3055c5595915522760e810d5630c0ecf295f9e16",
  iraq: "0xb0ba1dbc7137ec56b120a5690249a2be3cb96abb",
};

const fetchInBatches = async (listings, batchSize, signal) => {
  const all = [];
  for (let i = 0; i < listings.length; i += batchSize) {
    if (signal.aborted) break;
    const batch = listings.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(async (listing) => {
        try {
          const uri = resolveIPFS(listing.token?.metadataUri);
          if (!uri) return null;
          const meta = await fetch(uri, { signal }).then((r) => r.json());
          if (signal.aborted) return null;
          return {
            id: listing.id,
            name: meta.name || `Token #${listing.token.tokenId}`,
            year:
              meta.year ||
              meta.attributes?.find((a) => a.trait_type === "Year")?.value ||
              "Unknown",
            size:
              meta.size ||
              meta.attributes?.find((a) => a.trait_type === "Size")?.value ||
              "N/A",
            img: resolveIPFS(meta.image),
            prev: resolveIPFS(meta.preview || meta.image),
            price: (Number(listing.pricePerToken) / 1e18).toFixed(4),
            quantity: Number(listing.quantity) || 1,
            token: listing.token,
          };
        } catch (e) {
          if (e.name !== "AbortError") console.error(e);
          return null;
        }
      }),
    );
    all.push(...batchResults);
  }
  return all.filter(Boolean);
};

const fetchCollectionItems = async (collectionAddress, signal) => {
  const query = `{
    listings(
      where: { collection: "${collectionAddress.toLowerCase()}", active: true }
      first: 8
      orderBy: id
      orderDirection: asc
    ) {
      id pricePerToken quantity
      token { id tokenId metadataUri }
    }
  }`;

  const resp = await fetch(SUBGRAPH_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
    signal,
  });
  const { data } = await resp.json();
  const rawListings = data?.listings || [];

  return fetchInBatches(rawListings, 3, signal);
};

const ReArtworkSection = ({ title, subtitle, collectionName, collections }) => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentCursor, setCurrentCursor] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(5);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width > 1200) setItemsPerView(5);
      else if (width > 992) setItemsPerView(4);
      else if (width > 768) setItemsPerView(2);
      else setItemsPerView(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let collectionAddress = null;

    if (collections && collections.length > 0) {
      const selected = collections.find((col) =>
        collectionName
          ? col.name?.toLowerCase().trim() === collectionName.toLowerCase().trim()
          : !!col.name,
      );
      collectionAddress = selected?.id || null;
    } else {
      collectionAddress =
        COLLECTION_NAME_TO_ADDRESS[collectionName?.toLowerCase()] || null;
    }

    if (!collectionAddress) {
      setItems([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setItems([]);
    setLoading(true);

    fetchCollectionItems(collectionAddress, controller.signal)
      .then((fetched) => {
        if (!controller.signal.aborted) {
          setItems(fetched);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (e.name !== "AbortError") console.error(e);
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [isVisible, collectionName, collections]);

  const itemsToDisplay = useMemo(
    () => (items.length > 8 ? items.slice(-8) : items),
    [items],
  );

  const totalSections = useMemo(() => {
    if (window.innerWidth >= 992) {
      return itemsToDisplay.length <= itemsPerView ? 1 : 2;
    }
    return Math.max(Math.ceil(itemsToDisplay.length / itemsPerView), 1);
  }, [itemsToDisplay.length, itemsPerView]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onScroll = debounce(() => {
      const sectionWidth = container.offsetWidth;
      const scrollLeft = container.scrollLeft;
      const index =
        window.innerWidth >= 992
          ? scrollLeft < sectionWidth / 2 ? 0 : 1
          : Math.round(scrollLeft / sectionWidth);
      setCurrentCursor(Math.min(index, totalSections - 1));
    }, 50);
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [totalSections]);

  const handleCardClick = useCallback(() => {
    const params = new URLSearchParams();
    const address = COLLECTION_NAME_TO_ADDRESS[collectionName?.toLowerCase()];
    if (address) {
      params.set("country", collectionName);
      params.set("collection", address);
    } else {
      params.set("country", "Palestine");
      const matchedCol = collections?.find(
        (c) => c.name?.toLowerCase() === collectionName?.toLowerCase(),
      );
      params.set("collection", matchedCol ? matchedCol.id : collectionName);
    }
    navigate(`/reproduction/collections?${params.toString()}`);
  }, [collectionName, collections, navigate]);

  return (
    <section className="reartwork-section" ref={sectionRef}>
      <div className="reartwork-section__header">
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      <Scrollbars
        autoHeight
        autoHeightMin={260}
        autoHeightMax={320}
        autoHide={false}
        renderTrackVertical={() => <div />}
        renderThumbVertical={() => <div />}
        renderThumbHorizontal={(props) => (
          <div
            {...props}
            style={{ backgroundColor: "#000", borderRadius: "10px", height: "5px" }}
          />
        )}
      >
        <div className="reartwork-section__list" ref={containerRef}>
          {loading ? (
            <p className="loading-text">Loading artworks...</p>
          ) : itemsToDisplay.length > 0 ? (
            itemsToDisplay.map((item) => (
              <div
                className="reartwork-card"
                onClick={handleCardClick}
                style={{ cursor: "pointer" }}
                key={item.id}
              >
                <span className="reartwork-card__year">{item.year}</span>
                <div className="reartwork-card__image">
                  <img
                    src={item.prev || item.preview || item.img}
                    alt={item.name}
                    loading="eager"
                    decoding="async"
                  />
                  <div className="image-placeholder" />
                </div>
                <div className="reartwork-card__info">
                  <h4 className="reartwork-card__name">{item.name}</h4>
                  <p className="reartwork-card__size">
                    Print size – <span>{item.size}</span>
                  </p>
                </div>
              </div>
            ))
          ) : isVisible ? (
            <p>No artworks found in this collection</p>
          ) : null}
        </div>
      </Scrollbars>
    </section>
  );
};

export default React.memo(ReArtworkSection);
